import { useState, useRef, useEffect } from 'react';

// Import all specialized hooks
import { useVehiclePathColors } from './useVehiclePathColors';
import { useVehiclePathInitializer } from './useVehiclePathInitializer';
import { useVehiclePathActions } from './useVehiclePathActions';
import { useVehiclePathSelection } from './useVehiclePathSelection';
import { useVehiclePathSync } from './useVehiclePathSync';

/**
 * Vehicle Path Management Hook
 * Integrates all sub-hooks to provide complete path management functionality
 */
export const useVehiclePath = ({
  locations, 
  startId, 
  vehicleCount,
  setPath,
  path,
  currentModeRef,
  isCustomMode,
  maxCapacity,
  setMapPointSelect,
  pathComplete: externalPathComplete,
  setPathComplete: externalSetPathComplete
}) => {
  // Multi-vehicle path management
  const [vehiclePaths, setVehiclePaths] = useState([]);
  // Currently selected vehicle index
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(null);
  // Path completion status
  const [internalPathComplete, setInternalPathComplete] = useState(path.length > 1);
  
  // Use external or internal path completion status
  const pathComplete = externalPathComplete !== undefined ? externalPathComplete : internalPathComplete;
  const setPathComplete = externalSetPathComplete || setInternalPathComplete;
  
  // Add refs to track map point selection state
  const isHandlingMapPointRef = useRef(false);
  const lastSelectedPointRef = useRef(null);
  const activeCallbackIndexRef = useRef(null);
  
  // Use color management hook
  const { 
    getVehicleColor, 
    updateUsedColors 
  } = useVehiclePathColors();
  
  // Use path sync hook
  const { 
    updatePathThrottled 
  } = useVehiclePathSync({
    setPath,
    path,
    currentModeRef,
    isCustomMode
  });
  
  // Use path initializer hook
  const { 
    findLocationById 
  } = useVehiclePathInitializer({
    vehiclePaths,
    setVehiclePaths,
    vehicleCount,
    locations,
    startId,
    path,
    getVehicleColor,
    updateUsedColors,
    selectedVehicleIndex,
    setSelectedVehicleIndex,
    setMapPointSelect,
    activeCallbackIndexRef,
    setPathComplete
  });
  
  // Use path actions hook
  const { 
    selectVehiclePath,
    handleMapPointSelection,
    resetVehiclePath,
    resetAllPaths,
    removePointFromPath,
    addPointToSelectedVehicle,
    confirmAllPaths,
    undoLastPoint
  } = useVehiclePathActions({
    vehiclePaths,
    setVehiclePaths,
    selectedVehicleIndex,
    setSelectedVehicleIndex,
    startId,
    locations,
    setMapPointSelect,
    activeCallbackIndexRef,
    isHandlingMapPointRef,
    lastSelectedPointRef,
    updatePathThrottled,
    setPath,
    setPathComplete,
    findLocationById
  });
  
  // Use path selection hook
  const { 
    getRemainingPoints 
  } = useVehiclePathSelection({
    vehiclePaths,
    selectedVehicleIndex,
    locations,
    startId
  });
  
  // Cleanup callback on unmount
  useEffect(() => {
    return () => {
      if (setMapPointSelect) {

        setMapPointSelect(null);
        if (activeCallbackIndexRef.current !== null) {
          activeCallbackIndexRef.current = null;
        }
      }
    };
  }, [setMapPointSelect]);
  
  // Effect to handle mode changes - clear callbacks when leaving Custom Path mode
  useEffect(() => {
    if (!isCustomMode && setMapPointSelect) {

      setMapPointSelect(null);
      activeCallbackIndexRef.current = null;
    }
  }, [isCustomMode, setMapPointSelect]);
  
  return {
    vehiclePaths,
    selectedVehicleIndex,
    pathComplete,
    selectVehiclePath,
    handleMapPointSelection,
    resetVehiclePath,
    resetAllPaths,
    removePointFromPath,
    addPointToSelectedVehicle,
    confirmAllPaths,
    undoLastPoint,
    getRemainingPoints,
    isHandlingMapPointRef,
    lastSelectedPointRef
  };
}; 